import { Edit } from '@mui/icons-material'
import { Masonry } from '@mui/lab'
import { Button, Grid } from '@mui/material'
import { GreenText } from 'components'
import { useAuth, useLayout } from 'context'
import * as controllers from 'controllers'
import { CompanyDto } from 'dtos'
import { useRefresh } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import { Fragment, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import CompanyHeroCard from '../CompanyHeroCard'
import AttorneyCard from './AttorneyCard'
import BrokerInformationListCard from './BrokerInformationListCard'
import BuyerCompanyListCard from './BuyerCompanyListCard'
import ClusterConversionCompanyListCard from './ClusterConversionCompanyListCard'
import ClusterProgrammerCompanyListCard from './ClusterProgrammerCompanyListCard'
import CompanyAddEditDialog from './CompanyAddEditDialog'
import CompanyAddressesListCard from './CompanyAddressesListCard'
import ExportInfoListCard from './ExportInformationListCard'
import InsuranceCompanyListCard from './InsuranceCompanyListCard'
import RatesListCard from './RatesListCard'
import RegisteredImporterListCard from './RegisteredImporterListCard'
import TransporterInformationCard from './TransporterInformationListCard'
import InspectionCompanyCard from './InspectionCompanyCard'

export default function CompaniesId() {
  const { permissions } = useAuth()
  const { id } = useParams()
  const layout = useLayout()

  const [company, setCompany] = useState<CompanyDto>(new CompanyDto())
  const [isGettingCompany, setIsGettingCompany] = useState<boolean>(false)
  const [isUpdatingCompany, setIsUpdatingCompany] = useState<boolean>(false)
  const [companyAddEditDialogOpen, setCompanyAddEditDialogOpen] = useState<boolean>(false)
  const [refresh, handleRefresh] = useRefresh({
    root: 0
  })

  useEffect(() => {
    layout.setTitle('Company Details')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingCompany)
  }, [layout, isGettingCompany])

  useEffect(() => {
    if (id && id.toLowerCase() !== 'undefined' && id.toLowerCase() !== 'new') {
      setIsGettingCompany(true)
      controllers
        .getCompanyById(id)
        .then(response => {
          setCompany(response.value)
        })
        .finally(() => {
          setIsGettingCompany(false)
        })
    }
  }, [id])

  return (
    <Fragment key={refresh.root}>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <CompanyAddEditDialog
          initialValues={company}
          loading={isUpdatingCompany}
          onClose={() => {
            setCompanyAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsUpdatingCompany(true)
            controllers
              .updateCompany(values)
              .then(response => {
                setCompany(response.value)
                setCompanyAddEditDialogOpen(false)
                enqueueSnackbar('Company Saved Successfully!', { variant: 'success' })
              })
              .finally(() => {
                setIsUpdatingCompany(false)
              })
          }}
          open={companyAddEditDialogOpen}
        />
      )}

      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={12}>
          <CompanyHeroCard company={company}>
            {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
              <Button
                endIcon={<Edit />}
                onClick={() => {
                  setCompanyAddEditDialogOpen(true)
                }}
                variant='text'
              >
                EDIT
              </Button>
            )}
          </CompanyHeroCard>
        </Grid>

        <Grid item xs={12}>
          <Masonry columns={3} sequential sx={{ margin: 0 /* this should be in the theme, otherwise it doesn't line up with hero card. */ }}>
            <CompanyAddressesListCard
              company={company}
              handleRefresh={() => {
                handleRefresh('root')
              }}
            />

            {company.isAttorneyCompany && <AttorneyCard company={company} />}

            {company.isBrokerCompany && <BrokerInformationListCard company={company} />}

            {company.isBuyerCompany && <BuyerCompanyListCard company={company} />}

            {company.isClusterConversionCompany && <ClusterConversionCompanyListCard company={company} />}

            {company.isClusterProgrammerCompany && <ClusterProgrammerCompanyListCard company={company} />}

            {company.isExporterCompany && <ExportInfoListCard company={company} />}

            {company.isInspectionCompany && <InspectionCompanyCard company={company} />}

            {company.isInsuranceCompany && <InsuranceCompanyListCard company={company} />}

            {(company.isExporterCompany || company.isBuyerCompany) && <RatesListCard company={company} />}

            {company.isRegisteredImporterCompany && <RegisteredImporterListCard company={company} />}

            {(company.isInboundTransporterCompany || company.isOutboundTransporterCompany) && <TransporterInformationCard company={company} />}
          </Masonry>
        </Grid>

        <Grid item xs={12}>
          <GreenText>
            {`**Company Name**
            
Displays Company Name entered in the Edit Company Dialog.

**Chips:**

A Company can display one or more chips, indicating the company type(s). Possible chips 
include:

- Active
- Inactive
- Attorney Company
- Broker
- Buyer Company
- Exporter Company
- Cluster Conversion Company
- Cluster Programmer Company
- Inbound Transport Company
- Inspection Company
- Insurance Company
- Michigan Secretary Of State Branch
- Outbound Transporter Company
- Registered Importer Company
- Surety Company
- Title Destination Company
- Vehicle Destination Company

**Edit Button:**

Clicking the Edit button opens the Edit Company Dialog.

**Website:**

Displays companies Website entered in the Edit Company Dialog.

**Notes:**

Displays any Notes entered in the Edit Company Dialog.

**Cards:**

Cards are displayed based on the features toggled on for the company.

Cards are arranged in alphabetical order, displayed from left to right. After three columns, the next row begins, continuing this pattern.`}
          </GreenText>
        </Grid>
      </Grid>
    </Fragment>
  )
}
